import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCopilot } from '../../CopilotContext';
import { ChatListItem } from '../Sidebar/ChatListItem';
import { useChatList } from '../Sidebar/useChatList';
import { useNewChat } from '../Sidebar/useNewChat';

import styles from './ChatMenu.module.css';

/**
 * Panel-header conversation switcher — the "ECHOGPT ▾" title opens a popover
 * listing chats (with the rename/delete affordances from ChatListItem) plus a
 * "New chat" action. Replaces the old in-panel history rail.
 *
 * We roll our own popover rather than AntD Dropdown so the nested delete
 * Popconfirm (its own portal) can't be mistaken for an outside click.
 */
export function ChatMenu() {
  const { conversationUuid, switchConversation } = useCopilot();
  const { chats, isLoading, rename, remove } = useChatList();
  const newChat = useNewChat();

  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t))
        return;
      // The delete confirmation lives in an AntD portal outside our popover —
      // don't treat interacting with it as an outside click.
      if (
        t instanceof Element &&
        t.closest('.ant-popover, .ant-popconfirm, .ant-dropdown')
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleNewChat = useCallback(() => {
    newChat();
    setOpen(false);
  }, [newChat]);

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.title}>ECHOGPT</span>
        <DownOutlined
          className={`${styles.caret} ${open ? styles.caretOpen : ''}`}
        />
      </button>

      {open && (
        <div ref={popRef} className={styles.menu} aria-label="Conversations">
          <button
            type="button"
            className={styles.newChatBtn}
            onClick={handleNewChat}
          >
            <PlusOutlined />
            <span>New chat</span>
          </button>

          <div className={styles.scroll}>
            {isLoading && <div className={styles.empty}>Loading…</div>}
            {!isLoading && chats.length === 0 && (
              <div className={styles.empty}>No chats yet.</div>
            )}
            {chats.map((c) => (
              <ChatListItem
                key={c.uuid}
                chat={c}
                active={c.uuid === conversationUuid}
                onSelect={() => {
                  switchConversation(c.uuid);
                  setOpen(false);
                }}
                onRename={(title) => rename(c.uuid, title)}
                onDelete={() => remove(c.uuid)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
